<template>
  <div v-if="showContent && !!investmentProfile">
    <div class="regulatory-update-investment-profile">
      <HeadingDisplay
        title="Dados financeiros"
        head="Revisão de dados"
        subtitle="Essas informações são exigências da Comissão de Valores Mobiliários. Instrução CVM 506"
        class="regulatory-update-investment-profile__title"
      />

      <InputMoney
        v-model="investmentProfile.monthlyIncome"
        label="Renda mensal"
        helper-text="Informe o valor médio dos últimos 3 meses, caso não tenha renda fixa."
        :status="getInputStatus('monthlyIncome')"
        allow-zero
        @focus="removeError('monthlyIncome')"
      />
      <InputMoney
        v-model="investmentProfile.movableProperty"
        label="Bens móveis"
        :helper-text="
          getErrorMessage(
            'movableProperty',
            'Considere bens móveis como motos, carros, barcos e similares'
          )
        "
        :status="getInputStatus('movableProperty')"
        allow-zero
        @focus="removeError('movableProperty')"
      />
      <InputMoney
        v-model="investmentProfile.realEstate"
        label="Bens imóveis"
        helper-text="Considere bens móveis como apartamentos, casas, terrenos e similares."
        allow-zero
      />
      <InputMoney
        v-model="investmentProfile.totalInvested"
        label="Investimentos"
        helper-text="Considere poupança, fundos, renda fixa, renda variável e outros. Não considere previdência."
        :status="getInputStatus('totalInvested')"
        allow-zero
        @focus="removeError('totalInvested')"
      />
      <InputMoney
        v-model="investmentProfile.privatePension"
        label="Previdência privada"
        helper-text="Considere apenas planos privados."
        allow-zero
      />
      <ButtonContainer class="regulatory-update-investment-profile__footer">
        <template #button-primary>
          <ButtonPrimary
            :loading="isButtonLoading"
            size="large"
            label="Continuar"
            @click="handleButtonClick"
          />
        </template>
      </ButtonContainer>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import {
  HeadingDisplay,
  ButtonContainer,
  ButtonPrimary,
  InputMoney
} from '@warrenbrasil/nebraska-web';
import {
  getInvestmentProfile,
  putRegulatoryUpdate,
  saveInvestmentProfile
} from '../../services';
import { InputStatusType, InvestmentProfile } from '../../types';
import yupValidation from './validate';
import { YupValidationErrors } from '@/modules/common/helpers/yup-validation';
import { parseApiErrorToScreenError } from '../../helpers/parse-api-error-to-screen-error';
import {
  trackEvent,
  trackContinue,
  trackError,
  trackView
} from './track-events';
import { EventKnownProperties } from '@warrenbrasil/web-analytics';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { useRegulatoryUpdateStore } from '@/modules/account/views/RegulatoryUpdate/store/regulatoryUpdateStore';

@Component({
  components: { HeadingDisplay, ButtonContainer, ButtonPrimary, InputMoney }
})
export default class RegulatoryUpdateInvestmentProfile extends Vue {
  @Prop({ type: Boolean, required: true })
  public showContent!: boolean;

  @Emit()
  private setIsLoading(_isLoading: boolean) {}

  @Emit()
  private setHasError(_hasError: boolean) {}

  @Emit()
  private goToNextPage() {}

  @Emit()
  private notifyError(_message?: string) {}

  @Emit()
  private changeTracker(
    _newTracker: (remainingProps: EventKnownProperties) => void
  ) {}

  public investmentProfile: InvestmentProfile | null = null;

  public isButtonLoading = false;

  public errors: YupValidationErrors = {};

  private readonly regulatoryUpdateStore = useRegulatoryUpdateStore();

  mounted() {
    this.fetchInvestmentProfile();
    this.changeTracker(trackEvent);
  }

  private async fetchInvestmentProfile() {
    this.setIsLoading(true);
    this.setHasError(false);

    try {
      this.investmentProfile = await getInvestmentProfile();
      trackView();
    } catch (_e) {
      this.setHasError(true);
    }

    this.setIsLoading(false);
  }

  public getInputStatus(key: string) {
    return this.errors[key] ? InputStatusType.Error : InputStatusType.Default;
  }

  public getErrorMessage(key: string, defaultMessage = '') {
    return this.errors[key] ? this.errors[key][0] : defaultMessage;
  }

  public removeError(key: string) {
    delete this.errors[key];
  }

  public async handleButtonClick() {
    await this.validateErrors();

    if (yupValidation.isValid()) {
      this.isButtonLoading = true;
      try {
        if (this.enableRegulatoryUpdateBureau) {
          this.regulatoryUpdateStore.setInvestmentProfile(
            this.investmentProfile! as unknown as Partial<InvestmentProfile>
          );

          await putRegulatoryUpdate({
            personalProfile: this.regulatoryUpdateStore.personalProfile!,
            address: {
              ...this.regulatoryUpdateStore.address!,
              complement: this.regulatoryUpdateStore.address!.complement || '',
              zipcode: this.regulatoryUpdateStore.address!.zipcode.replace(
                '-',
                ''
              ),
              number: String(this.regulatoryUpdateStore.address!.number)
            },
            investmentProfile: {
              monthlyIncome: Number(
                this.regulatoryUpdateStore.investmentProfile!.monthlyIncome
              ),
              totalInvested: Number(
                this.regulatoryUpdateStore.investmentProfile!.totalInvested
              ),
              movableProperty: Number(
                this.regulatoryUpdateStore.investmentProfile!.movableProperty
              ),
              realEstate: Number(
                this.regulatoryUpdateStore.investmentProfile!.realEstate
              ),
              privatePension: Number(
                this.regulatoryUpdateStore.investmentProfile!.privatePension
              ),
              isProfessionalInvestor:
                this.regulatoryUpdateStore.investmentProfile!
                  .isProfessionalInvestor,
              isQualifiedInvestor:
                this.regulatoryUpdateStore.investmentProfile!
                  .isQualifiedInvestor
            },
            ...(this.enableFATCA && {
              usPerson: this.regulatoryUpdateStore.usPerson || null,
              taxResidence: this.regulatoryUpdateStore.taxResidence || null
            })
          });
        } else {
          await saveInvestmentProfile(this.investmentProfile!);
        }
        trackContinue();
        this.goToNextPage();
      } catch (e: any) {
        if (e?.error?.message?.value === 'InvalidInput') {
          this.errors = parseApiErrorToScreenError(e.error.locations);
        } else if (e?.error?.message?.value === 'ExpiredPassword') {
          this.$router.push({
            name: 'regulatoryUpdateVerifyPassword',
            query: { cameFrom: 'regulatoryUpdateInvestmentProfile' }
          });
        } else {
          trackError(e?.error?.message?.formatted);
          this.notifyError(e?.error?.message?.formatted);
        }
      }
      this.isButtonLoading = false;
    }

    Object.values(this.errors).forEach((v: string[]) => trackError(v[0]));
  }

  private async validateErrors() {
    try {
      await yupValidation.validate(this.investmentProfile);
    } catch (_e) {}

    this.errors = yupValidation.getAllErrors();
  }

  get enableRegulatoryUpdateBureau() {
    return FeatureFlagsModule.isEnabled(FeatureFlags.REGULATORY_UPDATE_BUREAU);
  }

  get enableFATCA() {
    return FeatureFlagsModule.isEnabled(
      FeatureFlags.ENABLE_FATCA_IN_REGULATORY_UPDATE
    );
  }
}
</script>

<style lang="less" scoped>
.regulatory-update-investment-profile {
  padding-bottom: @size-spacing-x1800;
  display: flex;
  flex-direction: column;
  gap: @size-spacing-x500;

  &__title {
    margin-bottom: @size-spacing-x700;
  }

  &__footer {
    position: fixed;
    left: 0;
    bottom: 0;
    background-color: @background-secondary;
    z-index: 1;
  }
}
</style>
